import React, { useEffect, useState } from "react";
import ImageUploader from "components/ImageUploader";
import Input from "components/Input";
import { useTranslation } from "react-i18next";
import SimpleSelect from "components/SimpleSelect";
import { SuggestOUT } from "api-client";
import SearchInput from "components/SearchInput";
import { M2_BGF_UNIT, M2_NRF_UNIT, placeDetailsToString } from "utils";
import { observer } from "mobx-react-lite";
import { buildingStore } from "store/Building/BuildingStore";
import { buildingSettingsStore } from "../BuildingSettingsStore";
import Checkbox from "components/Checkbox";
import CustomDatePicker from "components/DatePicker";
import TextInput from "components/TextInput";
import moment from "moment";
import clsx from "clsx";

function BuildingInformation({ type }: { type: "add" | "edit" }) {
  const { t } = useTranslation();
  const [selectedLocationSuggestion, setSelectedLocationSuggestion] =
    useState<SuggestOUT>();
  const { buildingTypes } = buildingStore;
  const { settings, showMandatoryAlert } = buildingSettingsStore;

  const onRemoveImages = React.useCallback((id: string) => {
    buildingSettingsStore.setImagesToRemove([
      ...buildingSettingsStore.imagesToRemove,
      id,
    ]);
  }, []);

  const [locationSuggestions, setLocationSuggestions] =
    useState<Array<SuggestOUT>>();

  useEffect(() => {
    if (!selectedLocationSuggestion) {
      return;
    }
    buildingSettingsStore
      .locationPlaceDetails(selectedLocationSuggestion)
      .then((placeDetails) => {
        placeDetails &&
          buildingSettingsStore.setSettings({
            ...settings,
            address: {
              ...settings?.address,
              ...placeDetails,
            },
            string_address: placeDetailsToString(placeDetails),
          });
      });
  }, [selectedLocationSuggestion]);

  function imageUploaderSection() {
    return (
      <div className="pb-1">
        {type === "edit" && (
          <div className="text-sm text-gray-700 pb-5">
            {t("addBuilding.photosInformations")}
          </div>
        )}
        <ImageUploader
          name="images"
          multiple
          labelName={t("commons.photos")}
          dragMessage={t("buildingSettings.dragAndDropMessage")}
          onChange={(value) =>
            buildingSettingsStore.onChangeValue(value, "selectedImages")
          }
          images={settings?.images}
          onRemove={onRemoveImages}
        />
      </div>
    );
  }

  const searchAddress = async (value: string, name?: string) => {
    name && buildingSettingsStore.onChangeValue(value, name);
    !value && buildingSettingsStore.onChangeValue({}, "address");
    await buildingSettingsStore
      .fetchLocationSuggestions(value)
      .then((response) => response && setLocationSuggestions(response));
  };

  const onChangeConstructionYear = (
    value: string | undefined | Date,
    name: string | undefined
  ) => {
    const year = Number(moment(value).format("yyyy"));
    buildingSettingsStore.onChangeValue(year, name);
  };

  const onChangeIsExisting = (value: boolean) => {
    buildingSettingsStore.onChangeValue(value, "is_existing");
    if (value) {
      buildingSettingsStore.onChangeValue("", "upcoming_operation");
    } else {
      buildingSettingsStore.onChangeValue("1", "upcoming_operation");
    }
  };

  return (
    <div>
      {type === "edit" && (
        <div className="text-lg font-semibold mb-2">
          {t("buildingSettings.buildingInformation")}
        </div>
      )}
      {imageUploaderSection()}
      <div
        className={clsx("flex justify-between gap-4 w-full mb-4", {
          "flex-col": type === "add",
        })}
      >
        <Input
          labelName="Name"
          name="name"
          required
          onChange={buildingSettingsStore.onChangeValue}
          containerClassName="w-full"
          value={settings?.name ?? ""}
          showMandatoryAlert={showMandatoryAlert}
          errorMessage={t("addBuilding.nameErrorMsg")}
          placeholder={t("buildingSettings.buildingNamePlaceholder")}
          testID="nameofbuilding_field"
          noMargin
        />
        <SearchInput
          labelName={t("addBuilding.address")}
          name="string_address"
          onChange={searchAddress}
          onSelect={setSelectedLocationSuggestion}
          placeholder={t("buildingSettings.addressPlaceholder")}
          items={locationSuggestions}
          value={settings?.string_address ?? ""}
          testID="addressbuilding_field"
          containerClassName="w-full"
          clearOnUnFocus
          noMargin
        />
      </div>
      <div
        className={clsx("flex justify-between gap-4 w-full items-start mb-4", {
          "flex-col": type === "add",
        })}
      >
        <SimpleSelect
          labelName={t("addBuilding.buildingType")}
          options={buildingTypes}
          name="building_type_id"
          onChange={buildingSettingsStore.onChangeValue}
          placeholder={t("buildingSettings.pleaseSelectBuildingType")}
          current={settings?.building_type_id}
          testID="buildingtype_filed"
          containerClassName="w-full"
          noMargin
        />
        <Checkbox
          labelElement={t("buildingSettings.isAnExistingBuilding")}
          containerClassName="w-full"
          checked={Boolean(settings?.is_existing)}
          onChange={(e) => onChangeIsExisting(e.currentTarget.checked)}
        />
      </div>
      <div
        className={clsx("flex justify-between gap-4 w-full items-start mb-4", {
          "flex-col": type === "add",
        })}
      >
        <CustomDatePicker
          value={
            settings?.construction_year
              ? String(settings?.construction_year)
              : ""
          }
          name="construction_year"
          dateFormat="yyyy"
          placeholder="YYYY"
          yearPicker
          height="middle"
          onChange={onChangeConstructionYear}
          label={t("addBuilding.yearOfConstruction")}
        />
        <TextInput
          type="number"
          labelName={t("buildingSettings.grossFloorArea")}
          placeholder={t("buildingSettings.enterArea")}
          unit={M2_BGF_UNIT}
          name="gross_floor_area"
          min={1}
          required
          showMandatoryAlert={showMandatoryAlert}
          value={String(settings?.gross_floor_area ?? "")}
          handleChange={buildingSettingsStore.onChangeValue}
          testID="grossFloorArea_field"
          containerClass="w-full"
          height="middle"
          noMargin
          helper={t("buildingSettings.grossRoomAreaSubtitle")}
        />
      </div>
      <div
        className={clsx("flex gap-4 justify-between items-start", {
          "flex-col": type === "add",
        })}
      >
        <div className="w-full">
          <TextInput
            type="number"
            labelName={t("buildingSettings.netRoomArea")}
            containerClass="w-full"
            placeholder={t("buildingSettings.enterArea")}
            name="net_floor_area"
            unit={M2_NRF_UNIT}
            min={1}
            required
            showMandatoryAlert={showMandatoryAlert}
            value={String(settings?.net_floor_area ?? "")}
            handleChange={buildingSettingsStore.onChangeValue}
            testID="netarea_field"
            noMargin
            helper={t("buildingSettings.netRoomAreaSubtitle")}
          />
        </div>
        <div className="w-full">
          <TextInput
            type="number"
            labelName={t("buildingSettings.heatedArea")}
            containerClass="w-full"
            placeholder={t("buildingSettings.enterArea")}
            name="heated_area"
            unit="m²"
            min={1}
            value={String(settings?.heated_area ?? "")}
            handleChange={buildingSettingsStore.onChangeValue}
            testID="heated_area"
            noMargin
            helper={t("buildingSettings.heatedAreaTooltip")}
          />
        </div>
      </div>
    </div>
  );
}

export default observer(BuildingInformation);
